<script setup>
// One notification in the staff inbox (#141). Compact in the list, full when
// opened (`expanded`). Acknowledgement state is shown from the item's own
// fields, opened with who acknowledged and when (#142); the `actions` slot is
// where the Acknowledge button goes (views/Inbox.vue).
import { computed } from 'vue'

const props = defineProps({
  item: { type: Object, required: true },
  expanded: { type: Boolean, default: false }
})
defineEmits(['open'])

const d = __DICT__.inbox

const unread = computed(() => !props.item.readAt)
const when = (ms) => {
  if (!ms) return ''
  try {
    return new Date(ms).toLocaleString()
  } catch {
    return ''
  }
}
const sentAt = computed(() => when(props.item.createdAt))
const ackState = computed(() => {
  const n = props.item
  if (!n.acknowledgement || n.acknowledgement === 'none') return null
  if (n.closedAt) return { text: d.closed, cls: 'bg-green-lt' }
  if (n.acknowledgedAt) return { text: d.acknowledged, cls: 'bg-green-lt' }
  return { text: d.needsAcknowledgement, cls: 'bg-warning-lt' }
})
// "Acknowledged by X at time"; when everyone must acknowledge, their own time and how many have
const ackDetail = computed(() => {
  const n = props.item
  if (!n.acknowledgement || n.acknowledgement === 'none') return null
  const lines = []
  if (n.acknowledgement === 'everyone') {
    if (n.closedAt) lines.push(`${d.everyoneAcknowledged} · ${when(n.closedAt)}`)
    else if (n.recipientCount) lines.push(`${n.acknowledgedCount || 0} / ${n.recipientCount} ${d.haveAcknowledged}`)
    if (n.acknowledgedAt) lines.push(`${d.youAcknowledged} · ${when(n.acknowledgedAt)}`)
  } else if (n.closedAt) {
    const mine = n.acknowledgedAt && n.acknowledgedAt === n.closedAt
    const by = mine ? d.you : (n.closedByName || d.someone)
    lines.push(`${d.acknowledgedBy} ${by} · ${when(n.closedAt)}`)
    if (!mine && n.acknowledgedAt) lines.push(`${d.youAcknowledged} · ${when(n.acknowledgedAt)}`)
  }
  return lines.length ? lines : null
})
</script>

<template>
  <div
      class="inbox-item list-group-item"
      :class="{ 'inbox-item-unread': unread, 'list-group-item-action': !expanded }"
      :role="expanded ? undefined : 'button'"
      :tabindex="expanded ? undefined : 0"
      @click="!expanded && $emit('open', item)"
      @keydown.enter="!expanded && $emit('open', item)"
  >
    <div class="d-flex w-100 justify-content-between align-items-start gap-2">
      <div class="fw-bold text-truncate" :class="{ 'text-wrap': expanded }">
        <span v-if="unread" class="badge bg-primary me-1 align-middle">{{ d.new }}</span>
        {{ item.title }}
      </div>
      <small class="text-secondary text-nowrap">{{ sentAt }}</small>
    </div>
    <p class="mb-0 mt-1 inbox-item-body" :class="{ 'inbox-item-body-clamped': !expanded }">{{ item.body }}</p>
    <div v-if="ackState" class="mt-2">
      <span class="badge" :class="ackState.cls">{{ ackState.text }}</span>
      <div v-if="expanded && ackDetail" class="mt-1">
        <small v-for="line in ackDetail" :key="line" class="d-block text-secondary">{{ line }}</small>
      </div>
    </div>
    <div v-if="expanded" class="d-flex flex-wrap gap-2 mt-3">
      <slot name="actions"></slot>
      <RouterLink v-if="item.link" :to="item.link" class="btn btn-primary btn-sm">{{ d.open }}</RouterLink>
    </div>
  </div>
</template>

<style scoped>
.inbox-item {
  font-size: 0.875rem;
}

.inbox-item-unread {
  border-left: 3px solid var(--tblr-primary);
}

.inbox-item-body {
  white-space: pre-line;
}

.inbox-item-body-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
